import { FC } from 'react';
import { Theme, Box, Typography, makeStyles, Container, Grid, Divider } from '@material-ui/core';
import Link from 'next/link';
import { privacyAppPolicyLinks } from 'config/routes/routes';
import { useRouter } from 'next/router';

const providers = [
  {
    provider: 'Cloud hosting providers for the GPM servers and database',
    receives: 'Account, work, photo, and location data',
    purpose: 'Hosting the GPM backend',
  },
  {
    provider: 'DigitalOcean (object storage)',
    receives: 'Uploaded photos and their metadata',
    purpose: 'Storing the company media library',
  },
  {
    provider: 'Expo (EAS Update)',
    receives: 'Device platform, App runtime version, update channel, and IP address',
    purpose: 'Delivering over-the-air updates to the App',
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(5, 0, 20),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(20, 0),
    },
  },
  linksBox: {
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(7, 5, 4),
    position: 'sticky',
    top: 120,
  },
  link: {
    marginBottom: theme.spacing(3),
    fontWeight: 500,
    cursor: 'pointer',
    paddingLeft: 13,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  activeLink: {
    color: '#ED6725',
    borderLeft: '3px solid #ED6725',
    fontWeight: 600,
  },
  contentBox: {
    backgroundColor: theme.palette.common.white,
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
    padding: theme.spacing(7, 5, 4),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
  },
  subtitle: {
    fontWeight: 600,
    fontSize: 18,
  },
  sectionBox: {
    marginBottom: theme.spacing(7),
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',

    [theme.breakpoints.down('xs')]: {
      overflowX: 'visible',
    },
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #E1E1E1',
    tableLayout: 'fixed',
    display: 'block',

    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },

  tableHeader: {
    backgroundColor: '#F5F5F5',
  },

  tableHeaderCell: {
    padding: theme.spacing(2.5, 2.5),
    borderBottom: '1px solid #E1E1E1',
    textAlign: 'left',

    fontSize: 15,
    lineHeight: '20px',
    fontWeight: 600,
    color: '#4F5260',

    '&:first-child': {
      width: '33%',
    },

    '&:nth-child(2)': {
      width: '37%',
    },

    '&:last-child': {
      width: '30%',
    },
  },

  tableRow: {
    '&:not(:last-child)': {
      borderBottom: '1px solid #E1E1E1',
    },
  },

  tableCell: {
    padding: theme.spacing(2, 2.5),
    verticalAlign: 'top',

    fontSize: 15,
    lineHeight: '22px',
    fontWeight: 400,
    color: '#5F6170',
  },

  // Mobile
  mobileList: {
    display: 'none',

    [theme.breakpoints.down('xs')]: {
      display: 'block',
      flexDirection: 'column',
      gap: theme.spacing(1), // 8px
    },
  },

  mobileCard: {
    padding: theme.spacing(2.5, 2),
    border: '1px solid #E1E1E1',
    borderBottom: '1px solid #E1E1E1',
    margin: '12px 0px',
  },

  mobileProvider: {
    marginBottom: theme.spacing(1.75),

    fontSize: 16,
    lineHeight: '21px',
    fontWeight: 600,
    color: '#4F5260',
  },

  mobileField: {
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },

  mobileLabel: {
    marginBottom: theme.spacing(0.5),

    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#9699A5',
  },

  mobileValue: {
    fontSize: 15,
    lineHeight: '22px',
    fontWeight: 400,
    color: '#646775',
  },

  desktopTable: {
    display: 'table',

    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  contactInfo: {
    backgroundColor: '#F5F5F5',
    borderLeft: '3px solid #ED6725',
    padding: theme.spacing(3, 4),
  },
  email: {
    color: '#ED6725',
    textDecoration: 'underline !important',
    cursor: 'pointer',
    fontWeight: 500,

    '&:hover': {
      color: '#ED6725',
    },
  },
  divider: {
    backgroundColor: '#E5E5E5',
    marginBottom: 42,
  },
  headline: {
    color: '#4F5260',
    fontSize: 32,
    fontWeight: 600,
    marginBottom: 10,
  },
  list: {
    padding: '0 5px',
    listStyle: 'none',
    '& li': {
      position: 'relative',
      paddingLeft: theme.spacing(2),

      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '0.55em',
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: '#F58220',
      },
    },
  },
}));

const AppPrivacyPolicyContent: FC = () => {
  const classes = useStyles();
  const router = useRouter();

  const currentHash = router.asPath.split('#')[1];
  return (
    <Box className={classes.root}>
      <Container fixed>
        <Grid container spacing={5}>
          <Grid item lg={4} xs={12}>
            <Box className={classes.linksBox}>
              {privacyAppPolicyLinks.map(({ label, route }, index) => {
                const linkHash = route.split('#')[1];
                const isActive = currentHash === linkHash;

                return (
                  <Link href={route} key={index}>
                    <Typography className={`${classes.link} ${isActive ? classes.activeLink : ''}`}>{label}</Typography>
                  </Link>
                );
              })}
            </Box>
          </Grid>
          <Grid item lg={8} xs={12}>
            <Box className={classes.contentBox}>
              <Box className={classes.sectionBox}>
                <Typography className={classes.headline}>GPM Mobile App — Privacy Policy</Typography>
                <Typography>
                  <span style={{ color: '#90939E' }}>Effective date</span>: <b>October 15, 2026</b>
                </Typography>
                <Box mt={3} />
                <Typography>
                  This Privacy Policy describes how <b>Utilities One Inc</b> (“Utilities One”, “we”, “us”) collects, uses, and
                  shares information when you use the <b>GPM</b> mobile application for Android and iOS (package name
                  com.utilitiesone.gpmmobile, the “App”).
                </Typography>
                <Box mt={3} />
                <Typography id="collect">
                  GPM is an internal field-work tool. It is made available only to Utilities One employees and authorized
                  contractors who have been given a GPM account by the company. It is not offered to the general public, and you
                  cannot create an account from within the App.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title} id="consent">
                  1. Information we collect
                </Typography>
                <Typography className={classes.subtitle}>1.1 Account and sign-in information</Typography>
                <Box mt={3} />
                <Typography>
                  To use the App you sign in with the email address and password of your company-issued GPM account. When you sign
                  in, the App sends to our servers:
                </Typography>
                <ul className={classes.list}>
                  <li>
                    <Typography>
                      Your email address and password (the password is used only to verify your identity and is never stored on
                      the device).
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      A randomly generated device identifier and the device platform (Android or iOS), so that the company can see
                      which devices are signed in and revoke a device’s access if it is lost or stolen.
                    </Typography>
                  </li>
                </ul>
                <Typography>
                  After sign-in, the App stores your name, email address, user ID, and session tokens in the operating system’s
                  secure storage on your device so you can keep working offline without signing in again.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography className={classes.subtitle}>1.2 Work data</Typography>
                <Box mt={3} />
                <Typography>
                  The App synchronizes the jobs, work items, and related records assigned to you from the GPM server and keeps a
                  copy in a local database on your device. Changes you make in the App (for example, updating a job) are queued on
                  the device and sent to the server when a connection is available. This work data belongs to Utilities One and is
                  the same information visible to authorized staff in the GPM web application.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography className={classes.subtitle}>1.3 Photos and camera</Typography>
                <Box mt={3} />
                <Typography>
                  With your permission, the App uses the camera and the photo library so you can attach photos to jobs. Photos you
                  capture or select are compressed, stored in the App’s private storage on your device, and uploaded to the
                  company’s media library on our servers when a connection is available. The App removes embedded camera metadata
                  (EXIF) from captured photos before storing them.
                </Typography>
                <Box mt={3} />
                <Typography>
                  If you deny camera or photo permission, you can still use the rest of the App but cannot attach photos.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography className={classes.subtitle}>1.4 Location</Typography>
                <Box mt={3} />
                <Typography>
                  With your permission, the App uses your device’s <b>approximate or precise location while the App is in use</b>{' '}
                  for two purposes:
                </Typography>
                <ul className={classes.list}>
                  <li>
                    <Typography>To show your position on the field map.</Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      To tag job photos with the coordinates where they were taken. The App also converts those coordinates to a
                      street address, city, and country, and uploads this information together with the photo so the company can
                      verify where field work was performed.
                    </Typography>
                  </li>
                </ul>
                <Typography>
                  The App <b>does not</b> collect location in the background and does not track your movements. If you deny
                  location permission, photos are uploaded without location data and the map simply does not show your position.
                </Typography>
              </Box>
              <Box className={classes.sectionBox}>
                <Typography className={classes.subtitle}>1.5 Technical and diagnostic information</Typography>
                <Box mt={3} />
                <ul className={classes.list}>
                  <li>
                    <Typography>
                      <b>Connectivity status</b>. The App checks whether the device is online in order to decide when to
                      synchronize. This status stays on the device.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      <b>App version and updates</b>. The App periodically checks for and downloads over-the-air updates to its
                      own code (see Section 3). The App version and update identifier are shown on the Settings screen.
                    </Typography>
                  </li>
                </ul>
                <Typography id="useInformation">
                  The App does <b>not</b> contain advertising, analytics, or third-party crash-reporting SDKs, does not access
                  your contacts, calendar, microphone, or messages, and does not collect any payment information.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  2. How we use the information
                </Typography>
                <Typography>We use the information described above to:</Typography>
                <ul className={classes.list}>
                  <li>
                    <Typography>Verify your identity and keep you signed in, including while offline.</Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      Deliver your assigned jobs and work records to the device and synchronize your changes back to the company’s
                      systems.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      Attach photos and their capture location to job records as documentation of field work.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>Show your position and work locations on the field map.</Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      Secure accounts and devices, detect misuse, and let administrators revoke a device’s access.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>Maintain and improve the App, including delivering updates.</Typography>
                  </li>
                </ul>
                <Typography id="shareInformation">
                  We do not use your information for advertising, profiling, or any purpose unrelated to Utilities One’s business
                  operations.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  3. How we share the information
                </Typography>
                <Typography>
                  We do not sell your personal information and do not share it with third parties for their own marketing.
                  Information is shared only as follows:
                </Typography>
                <Box mt={3} />
                <Typography>
                  <b>Within Utilities One</b>. Work data, photos, and photo locations you upload are visible to authorized
                  Utilities One staff (for example, supervisors, project managers, and administrators) in the GPM web application,
                  and may be included in project documentation provided to the customer for whom the work was performed.
                </Typography>
                <Box mt={3} />
                <Typography>
                  <b>Service providers</b>. We use the following categories of service providers, who process data on our behalf
                  and under our instructions:
                </Typography>
                <Box mt={3} />
                <table className={classes.table}>
                  <thead className={classes.tableHeader}>
                    <tr>
                      <th className={classes.tableHeaderCell}>Provider</th>
                      <th className={classes.tableHeaderCell}>What they receive</th>
                      <th className={classes.tableHeaderCell}>Purpose</th>
                    </tr>
                  </thead>

                  <tbody>
                    {providers.map((item, index) => (
                      <tr className={classes.tableRow} key={index}>
                        <td className={classes.tableCell}>{item.provider}</td>
                        <td className={classes.tableCell}>{item.receives}</td>
                        <td className={classes.tableCell}>{item.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Mobile */}
                <div className={classes.mobileList}>
                  {providers.map((item, index) => (
                    <div className={classes.mobileCard} key={index}>
                      <div className={classes.mobileProvider}>{item.provider}</div>

                      <div className={classes.mobileField}>
                        <div className={classes.mobileLabel}>What they receive</div>

                        <div className={classes.mobileValue}>{item.receives}</div>
                      </div>

                      <div className={classes.mobileField}>
                        <div className={classes.mobileLabel}>Purpose</div>

                        <div className={classes.mobileValue}>{item.purpose}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <Box mt={3} />
                <Typography>
                  <b>Device platform services</b>. When a photo is tagged with a location, the App uses the location and geocoding
                  services built into the device operating system (Google on Android, Apple on iOS) to obtain coordinates and
                  convert them to an address. Those services are governed by Google’s and Apple’s own privacy policies.
                </Typography>
                <Box mt={3} />
                <Typography>
                  <b>Legal and safety</b>. We may disclose information if required by law, regulation, legal process, or
                  governmental request, or to protect the rights, property, or safety of Utilities One, its personnel, or others.
                </Typography>
                <Box mt={3} />
                <Typography id="retention">
                  <b>Business transfers</b>. If Utilities One is involved in a merger, acquisition, or sale of assets, information
                  may be transferred as part of that transaction.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  4. Data retention
                </Typography>
                <ul className={classes.list}>
                  <li>
                    <Typography>
                      <b>On your device</b>. Work data, queued changes, and locally stored photos remain on the device until they
                      are synchronized and the App’s local cache is cleared, until you sign out, or until you uninstall the App.
                      Session tokens are deleted when you sign out.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography id="security">
                      <b>On our servers</b>. Job records, photos, and related data are business records of Utilities One and are
                      retained for as long as needed for the project and to meet legal, contractual, and audit obligations.
                      Sign-in sessions and refresh tokens expire automatically and can be revoked by an administrator at any time.
                    </Typography>
                  </li>
                </ul>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  5. Security
                </Typography>
                <Typography>We protect your information with measures including:</Typography>
                <ul className={classes.list}>
                  <li>
                    <Typography>
                      All communication between the App and our servers is encrypted in transit using TLS (HTTPS).
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      Session tokens are stored in the operating system’s secure storage (Android Keystore / iOS Keychain), never
                      in plain files.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      Access tokens are short-lived and refreshed automatically; refresh tokens are rotated on every use and
                      stored on the server only in hashed form. Reuse of a stolen token revokes the session.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>Passwords are stored on the server only as salted hashes.</Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>Administrators can revoke any device’s access immediately.</Typography>
                  </li>
                </ul>
                <Typography id="choices">
                  No system is completely secure. If you believe your account or device has been compromised, contact us
                  immediately using the details in Section 9.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  6. Your choices and rights
                </Typography>
                <ul className={classes.list}>
                  <li>
                    <Typography>
                      <b>Permissions</b>. You can grant or revoke the camera, photo, and location permissions at any time in your
                      device settings. The App continues to work with reduced functionality.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      <b>Sign out</b>. Signing out removes your session and locally cached data from the device.
                    </Typography>
                  </li>
                  <Box mt={2} />
                  <li>
                    <Typography>
                      <b>Access, correction, and deletion</b>. As an employee or contractor, your GPM account and the work data
                      associated with it are managed by Utilities One. To request access to, correction of, or deletion of your
                      personal information, or to close your account, contact us using the details in Section 9. We will respond
                      in accordance with applicable law. Note that some work records must be retained as business records even
                      after an account is closed.
                    </Typography>
                  </li>
                </ul>
                <Typography id="children">
                  Depending on where you live, you may have additional rights under applicable privacy laws, such as the right to
                  know what personal information we hold about you and the right to not be discriminated against for exercising
                  your rights. We honor these rights as required by law.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  7. Children
                </Typography>
                <Typography id="changes">
                  The App is a workplace tool intended for adults employed or contracted by Utilities One. It is not directed to
                  children under 18, and we do not knowingly collect personal information from children.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  8. Changes to this policy
                </Typography>
                <Typography id="contactUs">
                  We may update this Privacy Policy from time to time. The effective date at the top of the page shows when it was
                  last revised. Material changes will be communicated to App users through the App or by email.
                </Typography>
              </Box>
              <Divider className={classes.divider} />
              <Box className={classes.sectionBox}>
                <Typography variant="h4" className={classes.title}>
                  9. Contact us
                </Typography>
                <Typography>
                  If you have questions about this Privacy Policy or how your information is handled, contact:
                </Typography>
                <Box mt={3} />
                <Box className={classes.contactInfo}>
                  <Typography>
                    <b>Utilities One Inc</b>
                  </Typography>
                  <Box mt={1} />
                  <Typography>300 W Somerdale Rd., Suite 5E </Typography>
                  <Typography>Voorhees Township, NJ 08043</Typography>
                  <Typography>United States</Typography>
                  <Box mt={1} />
                  <Typography>
                    Email:{' '}
                    <Link href="mailto:ase@utilitiesone.com" className={classes.email}>
                      ase@utilitiesone.com
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
export default AppPrivacyPolicyContent;
